import { useQuery } from "@tanstack/react-query"
import { Copy, Plus, Search, Send } from "lucide-react"
import { useState } from "react"
import { useNavigate } from "react-router"

import { api, ErrorApi } from "@/api/cliente"
import { Insignia } from "@/componentes/ui/Insignia"
import { MenuAcciones, type OpcionMenu } from "@/componentes/ui/MenuAcciones"
import { tienePermiso } from "@/lib/autorizacion"
import { comoFechaHora } from "@/lib/fechas"
import { usarSesion } from "@/lib/sesion"

import { DialogoEnvioSistema2 } from "./DialogoEnvioSistema2"
import { DialogoMovimiento } from "./DialogoMovimiento"
import { aCentimos, monto } from "./dinero"
import { Extracto } from "./Extracto"
import { Terminacion } from "./Terminacion"
import type { Cuenta, ResumenCuenta } from "./tipos"

/**
 * La cuenta elegida, como en len-platform: una tarjeta con su nombre, su terminación,
 * el menú «Acciones» y las cifras, y debajo el extracto en su propia tarjeta.
 *
 * ⚠ **Las cifras salen del resumen del servidor, no de sumar el extracto.** El
 * extracto va con tope; entradas y salidas sumadas de lo que se ve cambiarían con el
 * tope y no con el dinero.
 *
 * ⚠ **El saldo corrido parte del MISMO saldo que se enseña arriba**, así que la
 * primera línea del extracto coincide siempre con la cifra grande.
 *
 * ⚠ **El menú lleva lo que el 3.0 hace con una cuenta, no lo que hace len-platform.**
 * Allí trae exportar a Excel, editar y dar de baja; aquí no existen. «Buscar en
 * Movimientos» es lo que el extracto necesita al lado: trae los más recientes, y lo
 * anterior se busca allí con fechas.
 */
export function FichaCuenta({ cuenta, divisa }: { cuenta: Cuenta; divisa: string }) {
  const usuario = usarSesion((e) => e.usuario)
  const puedeRegistrar = tienePermiso(usuario, "tesoreria:registrar")
  const navegar = useNavigate()
  const [registrando, setRegistrando] = useState(false)
  const [enviando, setEnviando] = useState(false)
  // Las cuentas que se replican en el 2.0 (hoy sólo Binance): sólo en ellas sale el botón.
  const puedeEnviar = tienePermiso(usuario, "tesoreria:enviar_sistema2")
  const replicadas = useQuery({
    queryKey: ["sistema2-envio", "cuentas"],
    queryFn: () => api.get<number[]>("/api/v1/sistema2-envio/cuentas"),
    enabled: puedeEnviar,
  })
  const [copiado, setCopiado] = useState<string | null>(null)

  /**
   * Copia la ficha de la cuenta: lo que hay que teclear en el banco.
   *
   * ⚠ **El texto lo redacta el SERVIDOR** (`tesoreria.texto_para_copiar`), que es
   * quien sabe qué pide cada clase: un pago móvil banco, cédula y titular; una
   * billetera su dirección y su red. Dos formatos, uno por pantalla, acabarían
   * diciendo cosas distintas.
   *
   * ⚠ **Se pide al pulsar y no antes.** Es la única ruta que devuelve el número
   * completo: pide el PIN de la cuenta y queda en la bitácora, así que traerlo «por
   * si acaso» al abrir la ficha dejaría un registro de algo que nadie miró.
   */
  async function copiar() {
    try {
      const datos = await api.get<{ texto: string }>(`/api/v1/cuentas-bancarias/${cuenta.id}/datos`)
      await navigator.clipboard.writeText(datos.texto)
      setCopiado("Datos copiados")
    } catch (error) {
      setCopiado(error instanceof ErrorApi ? error.message : "No se pudieron copiar")
    }
    setTimeout(() => setCopiado(null), 4000)
  }

  const resumen = useQuery({
    queryKey: ["resumen-cuenta", cuenta.id],
    queryFn: () => api.get<ResumenCuenta>(`/api/v1/cuentas-bancarias/${cuenta.id}/resumen`),
  })

  const opciones: OpcionMenu[] = [
    {
      id: "copiar",
      etiqueta: "Copiar datos",
      ayuda: "Titular, cédula y número, listos para pegar",
      icono: <Copy size={16} />,
      onSelect: copiar,
    },
    {
      id: "buscar",
      etiqueta: "Buscar en Movimientos",
      ayuda: "Con fechas, también lo anterior al extracto",
      icono: <Search size={16} />,
      onSelect: () => navegar(`/tesoreria/movimientos?cuenta=${cuenta.id}`),
    },
    // Una cuenta inactiva no recibe movimientos: el servidor lo permitiría, pero es
    // justo lo que «inactiva» quiere decir.
    ...(puedeRegistrar && cuenta.activo
      ? [
          {
            id: "registrar",
            etiqueta: "Registrar movimiento",
            ayuda: "Intereses, comisiones, correcciones",
            icono: <Plus size={16} />,
            onSelect: () => setRegistrando(true),
            separador: true,
          },
        ]
      : []),
    ...(puedeEnviar && (replicadas.data ?? []).includes(cuenta.id)
      ? [
          {
            id: "enviar-2",
            etiqueta: "Enviar al 2.0",
            ayuda: "Replica los movimientos para que los saldos coincidan",
            icono: <Send size={16} />,
            onSelect: () => setEnviando(true),
          },
        ]
      : []),
  ]

  return (
    // ⚠ `grid-cols-1` —`minmax(0, 1fr)`— y no una rejilla a secas: con la columna
    // implícita, el extracto la ensanchaba hasta su ancho mínimo y en un teléfono la
    // ficha entera se salía por la derecha. Así la tabla desplaza dentro de su caja.
    <div className="grid grid-cols-1 gap-4">
      <section className="tarjeta" aria-label={`Cuenta ${cuenta.nombre}`}>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className="flex flex-wrap items-center gap-x-2 gap-y-1 text-lg font-semibold">
              {cuenta.nombre}
              {cuenta.ultimos4 !== null && (
                <span className="text-sm font-normal" style={{ color: "var(--fg-muted)" }}>
                  <Terminacion ultimos4={cuenta.ultimos4} />
                </span>
              )}
              {!cuenta.activo && <Insignia tono="neutro">Inactiva</Insignia>}
            </h2>
            <p className="text-sm" style={{ color: "var(--fg-muted)" }}>
              {cuenta.banco ?? "Sin banco"} · {cuenta.tipo} · {divisa}
            </p>
          </div>
          <MenuAcciones opciones={opciones} />
        </div>

        {copiado && (
          <p role="status" className="mt-2 text-xs" style={{ color: "var(--fg-muted)" }}>
            {copiado}
          </p>
        )}

        {resumen.isPending && (
          <p className="mt-4 text-sm" style={{ color: "var(--fg-muted)" }}>
            Cargando…
          </p>
        )}
        {resumen.isError && (
          // Un cero aquí se leería como «la cuenta está vacía», que es otra cosa.
          <p role="alert" className="mt-4 text-sm" style={{ color: "var(--destructive)" }}>
            No se pudo calcular el saldo de esta cuenta
          </p>
        )}
        {resumen.data && (
          <>
            <dl
              // Por el ancho de la ficha y no por el de la pantalla: al lado de la
              // lista queda estrecha aunque el monitor sea grande.
              className="mt-4 grid grid-cols-[repeat(auto-fit,minmax(9rem,1fr))] gap-4 border-t pt-4"
              style={{ borderColor: "var(--border)" }}
            >
              <Cifra
                etiqueta="Saldo"
                valor={monto(resumen.data.saldo)}
                divisa={divisa}
                fuerte
                color={aCentimos(resumen.data.saldo) < 0n ? "var(--destructive)" : undefined}
              />
              <Cifra
                etiqueta="Entradas"
                valor={monto(resumen.data.entradas)}
                divisa={divisa}
                color="var(--success)"
              />
              <Cifra
                etiqueta="Salidas"
                valor={monto(resumen.data.salidas)}
                divisa={divisa}
                color="var(--destructive)"
              />
            </dl>
            <p className="mt-3 text-xs" style={{ color: "var(--fg-muted)" }}>
              {pie(resumen.data)}
            </p>
          </>
        )}
      </section>

      {resumen.data && (
        <section aria-label="Extracto">
          <Extracto cuenta={cuenta} saldo={resumen.data.saldo} divisa={divisa} />
        </section>
      )}

      {enviando && (
        <DialogoEnvioSistema2 cuenta={cuenta} divisa={divisa} onCerrar={() => setEnviando(false)} />
      )}
      <DialogoMovimiento
        cuenta={cuenta}
        divisa={divisa}
        abierto={registrando}
        onCerrar={() => setRegistrando(false)}
      />
    </div>
  )
}

/** La línea bajo las cifras: cuántos movimientos y cuándo fue el último. */
function pie(r: ResumenCuenta): string {
  if (r.movimientos === 0) return "Todavía sin movimientos."
  const cuantos = `${r.movimientos} movimiento${r.movimientos === 1 ? "" : "s"}`
  const ultimo = r.ultimo_movimiento ? `, el último el ${comoFechaHora(r.ultimo_movimiento)}` : ""
  return `${cuantos}${ultimo}. Las anuladas no cuentan en ninguna cifra.`
}

function Cifra({
  etiqueta,
  valor,
  divisa,
  fuerte = false,
  color,
}: {
  etiqueta: string
  valor: string
  divisa: string
  fuerte?: boolean
  color?: string
}) {
  return (
    <div className="min-w-0">
      <dt
        className="text-[11px] font-semibold tracking-wide uppercase"
        style={{ color: "var(--fg-muted)" }}
      >
        {etiqueta}
      </dt>
      <dd className={`tabular font-semibold ${fuerte ? "text-2xl" : "text-xl"}`} style={{ color }}>
        {valor}
        <span className="ml-1 text-xs font-normal" style={{ color: "var(--fg-muted)" }}>
          {divisa}
        </span>
      </dd>
    </div>
  )
}
